/**
 * Client-Side Structured Logger
 *
 * Provides leveled, structured logging for the browser client.
 * - debug / info:  only emitted when import.meta.env.DEV is true
 * - warn / error:  always emitted (all environments)
 *
 * Usage:
 *   import { logger } from '@utils/logger';
 *   const log = logger.createLogger('MyComponent');
 *   log.info('Loaded successfully', { RecordCount: 42 });
 *   log.error('Failed to save', { Error: err.message });
 *
 * The structured metadata follows the PascalCase convention
 * and mirrors the server logger JSON shape where practical.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  Timestamp: string;
  Level: string;
  Source: string;
  Message: string;
  Metadata?: Record<string, unknown>;
}

export interface Logger {
  debug: (message: string, metadata?: Record<string, unknown>) => void;
  info: (message: string, metadata?: Record<string, unknown>) => void;
  warn: (message: string, metadata?: Record<string, unknown>) => void;
  error: (message: string, metadata?: Record<string, unknown>) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const isDev = (): boolean => {
  try {
    return import.meta.env.DEV === true;
  } catch {
    return false;
  }
};

/**
 * Build a structured log entry matching the standard format.
 */
const buildEntry = (
  level: LogLevel,
  source: string,
  message: string,
  metadata?: Record<string, unknown>,
): LogEntry => {
  const entry: LogEntry = {
    Timestamp: new Date().toISOString(),
    Level: level.toUpperCase(),
    Source: source,
    Message: message,
  };
  if (metadata && Object.keys(metadata).length > 0) {
    entry.Metadata = metadata;
  }
  return entry;
};

// ---------------------------------------------------------------------------
// Output
// ---------------------------------------------------------------------------

/**
 * Emit a log entry to the browser console using the appropriate method.
 * In development, entries are printed in a human-friendly grouped format.
 * In production (warn/error only), entries are emitted as compact JSON so
 * they can be captured by any remote error-collection pipeline.
 */
const emit = (level: LogLevel, entry: LogEntry): void => {
  /* eslint-disable no-console */
  if (isDev()) {
    // Human-friendly output for local development
    const tag = `[${entry.Source}]`;
    if (entry.Metadata) {
      console[level](`${tag} ${entry.Message}`, entry.Metadata);
    } else {
      console[level](`${tag} ${entry.Message}`);
    }
  } else {
    // Structured JSON for production (warn & error only -- debug/info are suppressed before reaching here)
    console[level](JSON.stringify(entry));
  }
  /* eslint-enable no-console */
};

// ---------------------------------------------------------------------------
// Logger factory
// ---------------------------------------------------------------------------

/**
 * Create a scoped logger instance for a specific component or module.
 *
 * @param source  A short identifier for the component, e.g. 'LoginPage', 'apiClient'
 */
const createLogger = (source: string): Logger => ({
  debug(message: string, metadata?: Record<string, unknown>) {
    if (!isDev()) return;
    emit('debug', buildEntry('debug', source, message, metadata));
  },

  info(message: string, metadata?: Record<string, unknown>) {
    if (!isDev()) return;
    emit('info', buildEntry('info', source, message, metadata));
  },

  warn(message: string, metadata?: Record<string, unknown>) {
    emit('warn', buildEntry('warn', source, message, metadata));
  },

  error(message: string, metadata?: Record<string, unknown>) {
    emit('error', buildEntry('error', source, message, metadata));
  },
});

// ---------------------------------------------------------------------------
// Convenience root logger (source = 'App')
// ---------------------------------------------------------------------------

const rootLogger = createLogger('App');

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const logger = {
  /** Create a child logger scoped to a specific source / component. */
  createLogger,

  /** Root-level shorthand methods (source defaults to 'App'). */
  debug: rootLogger.debug,
  info: rootLogger.info,
  warn: rootLogger.warn,
  error: rootLogger.error,
};

export default logger;
